/**
 * React Query mutation hooks for carrier proposal plan group data
 * modifications (the "response" side of the BTX data model).
 */

import {
  QueryClient,
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from "@tanstack/react-query";
import { useApiClient } from "@platform/plan-grid-core";

import {
  deleteProposalPlan,
  moveProposalPlanGroup,
  restoreProposalPlan,
} from "./clientProposalPlanGroups";
import { proposalQueryKeys } from "./useProposals";
import {
  deleteProposalPlanGroup,
  dismissPlaceholderProposal,
  duplicateProposalCoverage,
  duplicateProposalPlanGroup,
  suppressProposalPlanGroupPads,
} from "./clientProposalPlanGroups";

import type { Proposal, ProposalPlanGroup } from "../types/proposal.types";
import type { PlaceholderMaterializeTarget } from "../adapters/proposalToGrid";

interface MoveProposalPlanGroupVariables {
  rfpId: string;
  groupId: string;
  rfpBenefitPlanGroupId: string;
}

/**
 * Shared mutation key for plan group moves. The Quotes page pauses proposals
 * polling while any move is pending, because a poll response still holds the
 * pre-move page and would overwrite the optimistic one.
 */
export const MOVE_PROPOSAL_PLAN_GROUP_MUTATION_KEY = [
  "moveProposalPlanGroup",
] as const;

interface MoveProposalPlanGroupContext {
  originalRfpBenefitPlanGroupId: string | undefined;
}

// A group's SBS page is decided solely by its rfpBenefitPlanGroupId, so
// rewriting it moves the carrier column between pages. Applied to every cached
// proposals scope for the RFP, since the move does not know which one is shown.
const setGroupPageInCache = (
  queryClient: QueryClient,
  rfpId: string,
  groupId: string,
  rfpBenefitPlanGroupId: string | undefined,
): void => {
  queryClient.setQueriesData<Proposal[]>(
    { queryKey: proposalQueryKeys.all(rfpId) },
    (old) =>
      old?.map((proposal) => ({
        ...proposal,
        coverages: proposal.coverages.map((coverage) => ({
          ...coverage,
          planGroups: coverage.planGroups.map((group) =>
            group.id === groupId ? { ...group, rfpBenefitPlanGroupId } : group,
          ),
        })),
      })),
  );
};

const findGroupPageInCache = (
  queryClient: QueryClient,
  rfpId: string,
  groupId: string,
): string | undefined =>
  queryClient
    .getQueriesData<Proposal[]>({ queryKey: proposalQueryKeys.all(rfpId) })
    .flatMap(([, proposals]) => proposals ?? [])
    .flatMap((proposal) => proposal.coverages)
    .flatMap((coverage) => coverage.planGroups)
    .find((group) => group.id === groupId)?.rfpBenefitPlanGroupId;

/**
 * Hook to move a carrier's proposal plan group to a different requested
 * benefit plan group's SBS page.
 *
 * Moves the group in the cached proposals immediately, because the round trip
 * (PATCH, then a full proposals refetch) takes seconds and the column would
 * otherwise sit on its old page. The proposals query is still refetched on
 * success, since placeholders on the source and target pages are recomputed
 * server-side (ADR-024). When moves overlap, only the last one to finish
 * refetches: an earlier refetch would return the pre-move page of a group
 * whose move is still pending.
 */
export const useMoveProposalPlanGroup = (
  options?: Omit<
    UseMutationOptions<
      ProposalPlanGroup,
      Error,
      MoveProposalPlanGroupVariables,
      MoveProposalPlanGroupContext
    >,
    "onMutate"
  >,
): UseMutationResult<
  ProposalPlanGroup,
  Error,
  MoveProposalPlanGroupVariables,
  MoveProposalPlanGroupContext
> => {
  const queryClient = useQueryClient();
  const axiosInstance = useApiClient();

  if (!axiosInstance) {
    throw new Error(
      "useMoveProposalPlanGroup requires an axiosInstance configured in PlanDesignerProvider",
    );
  }

  const { onSuccess, onError, ...restOptions } = options || {};

  return useMutation({
    mutationKey: MOVE_PROPOSAL_PLAN_GROUP_MUTATION_KEY,
    mutationFn: ({ groupId, rfpBenefitPlanGroupId }) =>
      moveProposalPlanGroup(groupId, rfpBenefitPlanGroupId, axiosInstance),
    ...restOptions,
    onMutate: async ({ rfpId, groupId, rfpBenefitPlanGroupId }) => {
      // An in-flight refetch would land the pre-move page over the optimistic one.
      await queryClient.cancelQueries({
        queryKey: proposalQueryKeys.all(rfpId),
      });
      const originalRfpBenefitPlanGroupId = findGroupPageInCache(
        queryClient,
        rfpId,
        groupId,
      );
      setGroupPageInCache(queryClient, rfpId, groupId, rfpBenefitPlanGroupId);
      return { originalRfpBenefitPlanGroupId };
    },
    onError: (err, variables, context, ...otherArgs) => {
      if (context) {
        setGroupPageInCache(
          queryClient,
          variables.rfpId,
          variables.groupId,
          context.originalRfpBenefitPlanGroupId,
        );
      }

      if (onError) {
        onError(err, variables, context, ...otherArgs);
      }
    },
    onSuccess: (data, variables, ...otherArgs) => {
      // This move still counts as pending while onSuccess runs, so 1 means no
      // other move is waiting on its PATCH.
      const isLastPendingMove =
        queryClient.isMutating({
          mutationKey: MOVE_PROPOSAL_PLAN_GROUP_MUTATION_KEY,
        }) === 1;
      if (isLastPendingMove) {
        queryClient.invalidateQueries({
          queryKey: proposalQueryKeys.all(variables.rfpId),
        });
      }

      if (onSuccess) {
        onSuccess(data, variables, ...otherArgs);
      }
    },
  });
};

export const useDeleteProposalPlanGroup = (
  options?: UseMutationOptions<void, Error, { id: string }>,
): UseMutationResult<void, Error, { id: string }> => {
  const axiosInstance = useApiClient();

  if (!axiosInstance) {
    throw new Error("Delete Proposal Plan Group API is not mocked");
  }

  const { onSuccess, ...restOptions } = options || {};

  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      deleteProposalPlanGroup(id, axiosInstance),
    ...restOptions,
    onSuccess: (data, variables, ...otherArgs) => {
      if (onSuccess) {
        onSuccess(data, variables, ...otherArgs);
      }
    },
  });
};

export const useDismissPlaceholderProposal = (
  options?: UseMutationOptions<
    void,
    Error,
    {
      rfpId: string;
      target: PlaceholderMaterializeTarget;
    }
  >,
): UseMutationResult<
  void,
  Error,
  {
    rfpId: string;
    target: PlaceholderMaterializeTarget;
  }
> => {
  const axiosInstance = useApiClient();

  if (!axiosInstance) {
    throw new Error("Dismiss Placeholder Proposal API is not mocked");
  }

  const { onSuccess, ...restOptions } = options || {};

  return useMutation({
    mutationFn: ({
      rfpId,
      target,
    }: {
      rfpId: string;
      target: PlaceholderMaterializeTarget;
    }) => dismissPlaceholderProposal(rfpId, target, axiosInstance),
    ...restOptions,
    onSuccess: (data, variables, ...otherArgs) => {
      if (onSuccess) {
        onSuccess(data, variables, ...otherArgs);
      }
    },
  });
};

export const useSuppressPadSlots = (
  options?: UseMutationOptions<
    void,
    Error,
    { id: string; padSuppressed?: boolean }
  >,
): UseMutationResult<void, Error, { id: string; padSuppressed?: boolean }> => {
  const axiosInstance = useApiClient();

  if (!axiosInstance) {
    throw new Error("Suppress Pad Slots API is not mocked");
  }

  const { onSuccess, ...restOptions } = options || {};

  return useMutation({
    mutationKey: ["suppressPadSlots"],
    mutationFn: ({
      id,
      padSuppressed,
    }: {
      id: string;
      padSuppressed?: boolean;
    }) =>
      suppressProposalPlanGroupPads(id, axiosInstance, padSuppressed ?? true),
    ...restOptions,
    onSuccess: (data, variables, ...otherArgs) => {
      if (onSuccess) {
        onSuccess(data, variables, ...otherArgs);
      }
    },
  });
};

export const useDuplicateProposalPlanGroup = (
  options?: UseMutationOptions<ProposalPlanGroup, Error, { id: string }>,
): UseMutationResult<ProposalPlanGroup, Error, { id: string }> => {
  const axiosInstance = useApiClient();

  if (!axiosInstance) {
    throw new Error("Duplicate Benefit Plan Group API is not mocked");
  }

  const { onSuccess, ...restOptions } = options || {};

  return useMutation({
    mutationKey: ["duplicateProposalPlanGroup"],
    mutationFn: ({ id }: { id: string }) =>
      duplicateProposalPlanGroup(id, axiosInstance),
    ...restOptions,
    onSuccess: (data, variables, ...otherArgs) => {
      if (onSuccess) {
        onSuccess(data, variables, ...otherArgs);
      }
    },
  });
};

/**
 * Hook to duplicate an entire proposal coverage (all of its plan groups) into a single brand
 * new Proposal on the same carrier, producing one new, independent carrier column rather than a
 * sibling group inside the existing one.
 */
export const useDuplicateProposalCoverage = (
  options?: UseMutationOptions<Proposal, Error, { id: string }>,
): UseMutationResult<Proposal, Error, { id: string }> => {
  const axiosInstance = useApiClient();

  if (!axiosInstance) {
    throw new Error("Duplicate Proposal Coverage API is not mocked");
  }

  const { onSuccess, ...restOptions } = options || {};

  return useMutation({
    mutationKey: ["duplicateProposalCoverage"],
    mutationFn: ({ id }: { id: string }) =>
      duplicateProposalCoverage(id, axiosInstance),
    ...restOptions,
    onSuccess: (data, variables, ...otherArgs) => {
      if (onSuccess) {
        onSuccess(data, variables, ...otherArgs);
      }
    },
  });
};

export const useDeleteProposalPlan = (
  options?: UseMutationOptions<void, Error, { id: string }>,
): UseMutationResult<void, Error, { id: string }> => {
  const axiosInstance = useApiClient();

  if (!axiosInstance) {
    throw new Error("Delete Proposal Plan API is not mocked");
  }

  const { onSuccess, ...restOptions } = options || {};

  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      deleteProposalPlan(id, axiosInstance),
    ...restOptions,
    onSuccess: (data, variables, ...otherArgs) => {
      if (onSuccess) {
        onSuccess(data, variables, ...otherArgs);
      }
    },
  });
};

export const useRestoreProposalPlan = (
  options?: UseMutationOptions<void, Error, { id: string }>,
): UseMutationResult<void, Error, { id: string }> => {
  const axiosInstance = useApiClient();

  if (!axiosInstance) {
    throw new Error("Restore Proposal Plan API is not mocked");
  }

  const { onSuccess, ...restOptions } = options || {};

  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      restoreProposalPlan(id, axiosInstance),
    ...restOptions,
    onSuccess: (data, variables, ...otherArgs) => {
      if (onSuccess) {
        onSuccess(data, variables, ...otherArgs);
      }
    },
  });
};
